import {OrderIntakeStepFactories} from '@hconnect/tuc-processes/orderIntake/wizard/types'

import {IntervalsStep} from '../../shared/order-editor/components/fields/Intervals'
import {MaterialStep} from '../../shared/order-editor/components/fields/Material'
import {MaterialVariationsStep} from '../../shared/order-editor/components/fields/MaterialVariations/MaterialVariationsStep'
import {QuoteStep} from '../../shared/order-editor/components/fields/Quote/QuoteStep'
import {UnloadingMethodStep} from '../../shared/order-editor/components/fields/UnloadingMethod'
import {UsageStep} from '../../shared/order-editor/components/fields/Usage'
import {OrderIntakeDateStep} from '../components/fields/Date'
import {OrderIntakeQuantityStep} from '../components/fields/Quantity'
import {OrderIntakeTimeStep} from '../components/fields/Time/OrderIntakeTimeStep'
import {DetailsStep} from '../components/steps/DetailsStep/DetailsStep'

// Import from config files
import {DEFAULT_STEP_ORDER} from './stepConfig'
import {RmcOrderIntakeStepType} from './stepTypes'

/**
 * Type for RMC step configuration
 */
export interface RmcStepConfiguration {
  isRmcOrder: boolean
  /** Optional map of step enablement flags */
  enabledSteps?: Record<RmcOrderIntakeStepType, boolean>
  /** Optional map of step ordering */
  stepOrder?: Record<RmcOrderIntakeStepType, number>
}

export const rmcStepFactories: OrderIntakeStepFactories<
  RmcOrderIntakeStepType,
  RmcStepConfiguration
> = [
  {
    key: RmcOrderIntakeStepType.QUOTE,
    factory: ({stepConfiguration}) => {
      return {
        Component: QuoteStep,
        wizardItem: {
          title: 'Quote',
          description: 'Which quote would you like to use for this order?',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.QUOTE] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.QUOTE]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.QUOTE] !== false,
        key: RmcOrderIntakeStepType.QUOTE
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.MATERIAL,
    factory: ({stepConfiguration}) => {
      return {
        Component: MaterialStep,
        wizardItem: {
          title: 'Material',
          description: 'Select the material for your order',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.MATERIAL] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.MATERIAL]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.MATERIAL] !== false,
        key: RmcOrderIntakeStepType.MATERIAL
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.MIX_CHARACTERISTICS,
    factory: ({stepConfiguration}) => {
      return {
        Component: MaterialVariationsStep,
        wizardItem: {
          title: 'Material Variations',
          description: 'Configure material variations',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.MIX_CHARACTERISTICS] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.MIX_CHARACTERISTICS]
        },
        isEnabled:
          stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.MIX_CHARACTERISTICS] !== false,
        key: RmcOrderIntakeStepType.MIX_CHARACTERISTICS
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.QUANTITY,
    factory: ({stepConfiguration}) => {
      return {
        Component: OrderIntakeQuantityStep,
        wizardItem: {
          title: 'Quantity',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.QUANTITY] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.QUANTITY]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.QUANTITY] !== false,
        key: RmcOrderIntakeStepType.QUANTITY
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.USAGE,
    factory: ({stepConfiguration}) => {
      return {
        Component: UsageStep,
        wizardItem: {
          title: 'Usage',
          description: 'Select the intended usage for your concrete',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.USAGE] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.USAGE]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.USAGE] !== false,
        key: RmcOrderIntakeStepType.USAGE
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.UNLOADING_METHOD,
    factory: ({stepConfiguration}) => {
      return {
        Component: UnloadingMethodStep,
        wizardItem: {
          title: 'Pump Service',
          description: 'Order pump service or select unloading method',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.UNLOADING_METHOD] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.UNLOADING_METHOD]
        },
        isEnabled:
          stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.UNLOADING_METHOD] !== false,
        key: RmcOrderIntakeStepType.UNLOADING_METHOD
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.INTERVALS,
    factory: ({stepConfiguration}) => {
      return {
        Component: IntervalsStep,
        wizardItem: {
          title: 'Intervals',
          description: 'Configure delivery intervals and timing',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.INTERVALS] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.INTERVALS]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.INTERVALS] !== false,
        key: RmcOrderIntakeStepType.INTERVALS
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.DATE,
    factory: ({stepConfiguration}) => {
      return {
        Component: OrderIntakeDateStep,
        wizardItem: {
          title: 'Date',
          description: 'Select your preferred delivery date',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.DATE] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.DATE]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.DATE] !== false,
        key: RmcOrderIntakeStepType.DATE
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.TIME,
    factory: ({stepConfiguration}) => {
      return {
        Component: OrderIntakeTimeStep,
        wizardItem: {
          title: 'Time',
          description: 'Select your preferred delivery time',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.TIME] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.TIME]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.TIME] !== false,
        key: RmcOrderIntakeStepType.TIME
      }
    }
  },
  {
    key: RmcOrderIntakeStepType.DETAILS,
    factory: ({stepConfiguration}) => {
      return {
        Component: DetailsStep,
        wizardItem: {
          title: 'Details',
          order:
            stepConfiguration.stepOrder?.[RmcOrderIntakeStepType.DETAILS] ||
            DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.DETAILS]
        },
        isEnabled: stepConfiguration.enabledSteps?.[RmcOrderIntakeStepType.DETAILS] !== false,
        key: RmcOrderIntakeStepType.DETAILS
      }
    }
  }
]
